/**
 * SEO Composable for 't SpeelFabrik
 *
 * Provides centralized SEO utilities for consistent meta tags, Open Graph,
 * structured data, and canonical URL generation.
 */

interface SeoOptions {

  /** Page title (without site name suffix) */
  title: string

  /** Meta description (150-160 chars recommended) */
  description: string

  /** Relative path for canonical URL (e.g., '/kampen') */
  path?: string

  /** OG image path relative to site (e.g., '/images/og-home.jpg') */
  image?: string

  /** OG type: website, article */
  type?: 'website' | 'article'

  /** Keywords for meta tag (comma-separated string or array) */
  keywords?: string | string[]

  /** Robots directive */
  robots?: string

  /** Additional Open Graph properties */
  ogExtras?: Record<string, string>

  /** Don't index this page */
  noIndex?: boolean
}

interface CampSeoOptions {

  /** Camp name */
  name: string

  /** Camp description */
  description: string

  /** Camp slug for URL */
  slug?: string

  /** Camp ID for URL */
  id: string

  /** Camp start date (ISO string) */
  startDate?: string

  /** Camp end date (ISO string) */
  endDate?: string

  /** Camp location */
  location?: string

  /** Camp price */
  price?: number

  /** Camp image URL */
  image?: string

  /** Camp status */
  status?: string
}

export function useSeo() {
  const config = useRuntimeConfig()
  const route = useRoute()

  const siteUrl = computed(() => config.public.siteUrl || 'https://www.tspeelfabrik.be')
  const siteName = '\'t SpeelFabrik'
  const defaultImage = '/images/og-default.jpg'
  const defaultDescription = '\'t SpeelFabrik organiseert onvergetelijke kampen voor kinderen en jongeren. Ontdek onze zomerkampen, sportkampen en creatieve kampen!'

  /**
   * Get absolute URL for a path
   */
  const getAbsoluteUrl = (path: string): string => {
    const base = siteUrl.value.replace(/\/$/, '')
    const cleanPath = path.startsWith('/') ? path : `/${path}`

    return `${base}${cleanPath}`
  }

  /**
   * Get current page's absolute URL
   */
  const getCurrentUrl = (): string => {
    return getAbsoluteUrl(route.path)
  }

  /**
   * Set SEO meta tags for a page
   */
  const setSeoMeta = (options: SeoOptions) => {
    const {
      title,
      description,
      path,
      image = defaultImage,
      type = 'website',
      keywords,
      robots = 'index, follow',
      ogExtras = {},
      noIndex = false,
    } = options

    const canonicalUrl = path ? getAbsoluteUrl(path) : getCurrentUrl()
    const absoluteImage = image.startsWith('http') ? image : getAbsoluteUrl(image)
    const fullTitle = `${title} | ${siteName}`

    // Handle keywords (can be string or array)
    const keywordsString = Array.isArray(keywords) ? keywords.join(', ') : keywords

    // Set meta tags
    useSeoMeta({
      title,
      description,
      ogTitle: fullTitle,
      ogDescription: description,
      ogType: type,
      ogUrl: canonicalUrl,
      ogImage: absoluteImage,
      ogImageWidth: 1200,
      ogImageHeight: 630,
      ogSiteName: siteName,
      ogLocale: 'nl_BE',

      // Twitter Card
      twitterCard: 'summary_large_image',
      twitterTitle: fullTitle,
      twitterDescription: description,
      twitterImage: absoluteImage,

      // Additional meta
      robots: noIndex ? 'noindex, nofollow' : robots,

      // Additional OG tags
      ...ogExtras,
    })

    // Set keywords and canonical URL
    useHead({
      meta: keywordsString ? [{ name: 'keywords', content: keywordsString }] : [],
      link: [
        { rel: 'canonical', href: canonicalUrl },
      ],
    })
  }

  /**
   * Set SEO for camp detail pages with Event structured data
   */
  const setCampSeo = (camp: CampSeoOptions) => {
    const {
      name,
      description,
      id,
      startDate,
      endDate,
      location,
      price,
      image,
      status = 'published',
    } = camp

    const campPath = `/camp-details?id=${id}`
    const canonicalUrl = getAbsoluteUrl(campPath)

    const absoluteImage = image
      ? (image.startsWith('http') ? image : getAbsoluteUrl(image))
      : getAbsoluteUrl(defaultImage)

    const fullTitle = `${name} | Kamp | ${siteName}`
    const campDescription = description || `Ontdek ${name} bij 't SpeelFabrik. Een onvergetelijk kamp voor kinderen!`

    // Determine event status for schema
    const eventStatus = status === 'cancelled'
      ? 'EventCancelled'
      : status === 'postponed'
        ? 'EventPostponed'
        : 'EventScheduled'

    // Set meta tags (OG type should be 'website' - Event data goes in schema.org)
    useSeoMeta({
      title: name,
      description: campDescription,
      ogTitle: fullTitle,
      ogDescription: campDescription,
      ogType: 'website',
      ogUrl: canonicalUrl,
      ogImage: absoluteImage,
      ogImageWidth: 1200,
      ogImageHeight: 630,
      ogSiteName: siteName,
      ogLocale: 'nl_BE',

      // Twitter Card
      twitterCard: 'summary_large_image',
      twitterTitle: fullTitle,
      twitterDescription: campDescription,
      twitterImage: absoluteImage,

      robots: 'index, follow',
    })

    // Set keywords and canonical URL
    useHead({
      meta: [
        { name: 'keywords', content: `kamp, ${name}, kinderen, jongeren, ${location || 'België'}, speelfabrik` },
      ],
      link: [
        { rel: 'canonical', href: canonicalUrl },
      ],
    })

    // Return schema data for use with nuxt-schema-org
    return {
      '@type': 'Event' as const,
      name,
      'description': campDescription,
      'url': canonicalUrl,
      'image': absoluteImage,
      'startDate': startDate || undefined,
      'endDate': endDate || undefined,
      eventStatus,
      'eventAttendanceMode': 'OfflineEventAttendanceMode',
      'location': location
        ? {
          '@type': 'Place',
          'name': location,
          'address': {
            '@type': 'PostalAddress',
            'addressLocality': location,
            'addressCountry': 'BE',
          },
        }
        : undefined,
      'organizer': {
        '@type': 'Organization',
        'name': siteName,
        'url': siteUrl.value,
      },
      ...(price !== undefined && {
        offers: {
          '@type': 'Offer',
          'price': price.toString(),
          'priceCurrency': 'EUR',
          'availability': 'InStock',
          'url': canonicalUrl,
        },
      }),
    }
  }

  /**
   * Get Organization schema data
   */
  const getOrganizationSchema = () => ({
    '@type': 'Organization',
    'name': siteName,
    'alternateName': 't SpeelFabrik VZW',
    'url': siteUrl.value,
    'logo': getAbsoluteUrl('/images/logo.png'),
    'description': defaultDescription,
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'BE',
    },
    'sameAs': [
      // Add social media links here when available
      // 'https://facebook.com/speelfabrik',
      // 'https://www.instagram.com/vzwtspeelfabrik/',
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'availableLanguage': 'Dutch',
    },
  })

  /**
   * Get LocalBusiness schema data
   */
  const getLocalBusinessSchema = () => ({
    '@type': 'LocalBusiness',
    '@id': `${siteUrl.value}/#organization`,
    'name': siteName,
    'alternateName': 't SpeelFabrik VZW',
    'description': defaultDescription,
    'url': siteUrl.value,
    'logo': getAbsoluteUrl('/images/logo.png'),
    'image': getAbsoluteUrl('/images/og-default.jpg'),
    'priceRange': '€€',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'BE',
    },
  })

  /**
   * Generate breadcrumb schema
   */
  const getBreadcrumbSchema = (items: Array<{ name: string, path: string }>) => ({
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': getAbsoluteUrl(item.path),
    })),
  })

  return {
    siteUrl,
    siteName,
    defaultImage,
    defaultDescription,
    getAbsoluteUrl,
    getCurrentUrl,
    setSeoMeta,
    setCampSeo,
    getOrganizationSchema,
    getLocalBusinessSchema,
    getBreadcrumbSchema,
  }
}
